import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  RotateCcw, Plus, Search, Filter, RefreshCw, Eye, 
  CheckCircle2, Clock, XCircle, Loader2, Truck, Package
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorReturnList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [returns, setReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const loadReturns = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/vendor-returns/returns`);
      setReturns(response.data);
    } catch (error) {
      console.error('Error loading returns:', error);
      toast.error('Failed to load vendor returns');
    } finally {
      setLoading(false);
    }
  }, []);

  const filterReturns = useCallback(() => {
    let filtered = [...returns];
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.return_number?.toLowerCase().includes(term) ||
        r.vendor_id?.toLowerCase().includes(term) ||
        r.vendor_name?.toLowerCase().includes(term)
      );
    }
    setFilteredReturns(filtered);
  }, [returns, statusFilter, searchTerm]);

  useEffect(() => {
    loadReturns();
  }, [loadReturns]);

  useEffect(() => {
    filterReturns();
  }, [filterReturns]);

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'PENDING_APPROVAL': 'bg-yellow-100 text-yellow-800',
      'APPROVED': 'bg-orange-100 text-orange-800',
      'IN_TRANSIT': 'bg-orange-100 text-orange-800',
      'CREDIT_PENDING': 'bg-orange-100 text-orange-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT': return <Clock className="w-4 h-4" />;
      case 'COMPLETED': return <CheckCircle2 className="w-4 h-4" />;
      case 'IN_TRANSIT': return <Truck className="w-4 h-4" />;
      case 'CANCELLED': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="vendor-return-list">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <RotateCcw className="w-7 h-7 text-rose-600" />
              Vendor Returns
            </h1>
            <p className="text-sm text-gray-500">Return goods to vendors</p>
          </div>
          {canWrite('vendor_returns') && (
          <Button onClick={() => navigate('/vendor-returns/create')} className="bg-rose-600 hover:bg-rose-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Return
          </Button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by return number, vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="ALL">All Status</option>
                <option value="DRAFT">Draft</option>
                <option value="APPROVED">Approved</option>
                <option value="IN_TRANSIT">In Transit</option>
                <option value="CREDIT_PENDING">Credit Pending</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={loadReturns}>
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-rose-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading vendor returns...</p>
            </div>
          ) : filteredReturns.length === 0 ? (
            <div className="p-8 text-center">
              <RotateCcw className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No vendor returns found</p>
              {canWrite('vendor_returns') && (
              <Button onClick={() => navigate('/vendor-returns/create')} className="mt-4" variant="outline">
                Create Return
              </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credit Memo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredReturns.map((ret) => (
                    <tr key={ret.return_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-rose-600">{ret.return_number}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{ret.vendor_id}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[150px]">{ret.vendor_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ret.status)}`}>
                          {getStatusIcon(ret.status)}
                          {ret.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">{ret.total_items}</td>
                      <td className="px-4 py-3 text-right">{ret.total_quantity}</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(ret.total_value, ret.currency)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{ret.return_date}</td>
                      <td className="px-4 py-3">
                        {ret.credit_memo_number ? (
                          <span className="text-green-600 font-medium">{ret.credit_memo_number}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/vendor-returns/${ret.return_number}`)}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredReturns.length} of {returns.length} returns
        </div>
      </div>
    </Layout>
  );
};

export default VendorReturnList;
